import { FC, useMemo, useState } from 'react';
import { Box, Flex, Link, SkeletonText, Spinner, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { useApplicationQueryResults } from '~/core/shared/api/middle-office/application';
import { EMPTY_ARR, EMPTY_OBJ } from '~/core/shared/constants';
import { useTableParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { AnyDocument, Sorting } from '~/core/shared/types';
import { ReactTable } from './ReactTable.component';

type DocumentsQueryTableProps = {
  columns: ColumnDef<AnyDocument>[];
  uuid: string;
  definition: string;
  title: string;
  query: string;
  refreshableQuery?: boolean;
};

const getColumnsWithSkeletons = (
  columns: ColumnDef<AnyDocument>[],
  showQueryLoader: boolean,
): ColumnDef<AnyDocument>[] => {
  if (showQueryLoader) {
    return columns.map((column) => ({
      ...column,
      cell: () => <SkeletonText lineClamp={2} />,
    }));
  }

  return columns;
};

const defaultSorting: Sorting = { column: 'updated_at', direction: 'desc' };

export const DocumentsQueryTable: FC<DocumentsQueryTableProps> = ({
  uuid,
  definition,
  columns,
  title,
  query,
  refreshableQuery = false,
}) => {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const additionalParams = useMemo(() => {
    if (refreshCounter > 0) {
      return {
        refresh: '1',
        cb: String(refreshCounter),
      };
    }
    return undefined;
  }, [refreshCounter]);

  const { tableProps, searchParams } = useTableParams(
    defaultSorting,
    100,
    additionalParams,
  );

  const { isLoading, isFetching, queryResults, meta } =
    useApplicationQueryResults(
      uuid,
      definition || 'unknownDefinition',
      query,
      searchParams,
    );

  const refresh = useEventHandler(() => {
    setRefreshCounter((prevCounter) => prevCounter + 1);
  });

  const showQueryLoader = isLoading || isFetching;
  const showTableSkeleton = showQueryLoader || !queryResults;

  const tableData = useMemo(
    () =>
      showTableSkeleton
        ? (Array.from({ length: 3 }).fill(EMPTY_OBJ) as AnyDocument[])
        : (queryResults ?? EMPTY_ARR),
    [showTableSkeleton, queryResults],
  );

  const tableColumns: ColumnDef<AnyDocument>[] = useMemo(
    () => getColumnsWithSkeletons(columns, showTableSkeleton),
    [showTableSkeleton, columns],
  );

  return (
    <Box mb={12}>
      <Flex direction="row" justifyContent="space-between" py={6}>
        <Text fontWeight="bold" maxW="309px" w="30%">
          {title}
        </Text>
        {refreshableQuery && (
          <>
            {showQueryLoader && <Spinner />}
            {!showQueryLoader && <Link onClick={refresh}>Refresh</Link>}
          </>
        )}
      </Flex>
      <Box bg="white">
        <ReactTable
          columns={tableColumns}
          data={tableData}
          meta={meta}
          {...tableProps}
        />
      </Box>
    </Box>
  );
};
